import type { RailgunTokenBalanceMap } from './railgunTokens'

export enum PrivateTransferPopupPreflightCode {
  NO_PRIVATE_BALANCE = 'NO_PRIVATE_BALANCE',
  POI_REQUIRED = 'POI_REQUIRED',
  STATE_NOT_READY = 'STATE_NOT_READY'
}

export type PrivateBalanceSnapshotEntry = {
  hasSpendableBalance: boolean
  hasMissingPOI: boolean
  updatedAt: number
}

export type PrivateBalanceSnapshot = Record<string, PrivateBalanceSnapshotEntry>

export const PRIVATE_BALANCE_POPUP_SNAPSHOT_STORAGE_KEY = 'railgunPrivateTransferPopupSnapshotV1'
export const PRIVATE_BALANCE_BACKGROUND_SNAPSHOT_STORAGE_KEY =
  'railgunPrivateTransferBackgroundSnapshotV1'
export const PRIVATE_BALANCE_POPUP_SNAPSHOT_MAX_AGE_MS = 30 * 60 * 1000
const PRIVATE_TRANSFER_PREFLIGHT_ERROR_PREFIX = 'RAILGUN_PRIVATE_TRANSFER_PREFLIGHT:'

export function getPrivateBalanceSnapshotEntryKey(params: {
  accountAddr: string
  chainId: number
  tokenAddress: string
}): string {
  return `${params.accountAddr.toLowerCase()}|${params.chainId}|${params.tokenAddress.toLowerCase()}`
}

export function hasPositiveTokenAmount(
  tokenMap: RailgunTokenBalanceMap | undefined,
  chainId: number,
  tokenAddress: string
): boolean {
  const balances = tokenMap?.get(chainId)
  if (!balances?.length) return false
  const tokenLower = tokenAddress.toLowerCase()
  return balances.some((item) => item.tokenAddress.toLowerCase() === tokenLower && item.amount > 0n)
}

export function createPrivateTransferPopupPreflightError(
  code: PrivateTransferPopupPreflightCode
): Error {
  return new Error(`${PRIVATE_TRANSFER_PREFLIGHT_ERROR_PREFIX}${code}`)
}

const PRIVATE_TRANSFER_PREFLIGHT_CODES = new Set<string>(
  Object.values(PrivateTransferPopupPreflightCode)
)

export function getPrivateTransferPopupPreflightCode(
  err: unknown
): PrivateTransferPopupPreflightCode | null {
  const message = err instanceof Error ? err.message : String(err)
  if (!message.startsWith(PRIVATE_TRANSFER_PREFLIGHT_ERROR_PREFIX)) return null
  const code = message.slice(PRIVATE_TRANSFER_PREFLIGHT_ERROR_PREFIX.length)
  if (PRIVATE_TRANSFER_PREFLIGHT_CODES.has(code)) {
    return code as PrivateTransferPopupPreflightCode
  }
  return null
}

export function getPopupPreflightMessage(
  t: (key: string) => string,
  code: PrivateTransferPopupPreflightCode
): string {
  if (code === PrivateTransferPopupPreflightCode.NO_PRIVATE_BALANCE) {
    return t('You do not have private balance for this token yet. Shield this token first.')
  }
  if (code === PrivateTransferPopupPreflightCode.POI_REQUIRED) {
    return t(
      'Your private balance is not ready to send because POI sync is not complete yet. Please try again after sync finishes.'
    )
  }
  return t(
    'Private data is not ready in this request window yet. Open Private mode to sync, then try again.'
  )
}
